import React, { useEffect } from 'react'
import { ArrowUpRight } from 'lucide-react'
import Reveal from '../component/Reveal'
import Atmosphere from '../component/Atmosphere'
import YouTubeVideoWithThumbnail from '../component/FacadeVideo'

const openContact = () => window.dispatchEvent(new CustomEvent('pdv:open-contact'))

const Frame = ({ children }) => (
  <div className="card-surface ring-gradient rounded-2xl overflow-hidden">{children}</div>
)

const VideoFrame = ({ videoId, title }) => (
  <Frame>
    <div className="relative w-full h-60 bg-[#07021a]">
      <div className="absolute inset-0">
        <YouTubeVideoWithThumbnail videoId={videoId} title={title} />
      </div>
    </div>
  </Frame>
)

const ImgFrame = ({ src, alt }) => (
  <Frame>
    <img src={src} alt={alt} className="w-full h-60 object-cover" />
  </Frame>
)

const ServiceBlock = ({ id, index, title, items, media, reverse }) => (
  <Reveal as="section" id={id} className="scroll-mt-28 py-14 lg:py-20">
    <div className="container-pdv">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className={reverse ? 'lg:order-2' : ''}>
          <span className="eyebrow">Prestation {index}</span>
          <h2 className="mt-4 font-display font-extrabold leading-[1.05] text-[clamp(1.7rem,3.2vw,2.6rem)]">
            {title}
          </h2>
          <ul className="mt-7 space-y-3">
            {items.map((it, i) => (
              <li key={i} className="flex gap-3 text-lg text-[#c4b8e6]">
                <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-violet-bright shrink-0" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className={`space-y-5 ${reverse ? 'lg:order-1' : ''}`}>{media}</div>
      </div>
    </div>
  </Reveal>
)

const PagePrestations = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
    document.title = 'Nos prestations — Place des Victoires'
  }, [])

  const sections = [
    {
      id: 'accessibilité',
      index: '01',
      title: 'Accessibilité numérique & FALC',
      items: [
        'Audit de conformité RGAA',
        'Mise en accessibilité de site internet',
        'Traduction en Facile À Lire et à Comprendre (FALC)',
        'PDF accessibles',
        'Enregistrement audio',
      ],
      media: (
        <>
          <div className="grid grid-cols-2 gap-5">
            <Frame>
              <img
                src="/déclaration.PNG"
                alt="Déclaration d'accessibilité RGAA"
                className="w-full h-72 object-contain bg-white"
              />
            </Frame>
            <Frame>
              <img
                src="/schéma.PNG"
                alt="Schéma de mise en accessibilité"
                className="w-full h-72 object-contain bg-white"
              />
            </Frame>
          </div>
          <ImgFrame src="/livret.png" alt="Livret d'accueil accessible — Hôpital Foch" />
        </>
      ),
    },
    {
      id: 'multimedia',
      index: '02',
      title: 'Création multimédia',
      reverse: true,
      items: [
        'Vidéos publicitaires en motion design ou 3D',
        'Sites internet accessibles',
        'Plateformes e-learning et/ou participatives (LMS)',
        'Trophées digitaux en 2D/3D',
        'Design graphique des supports de communication en FALC',
      ],
      media: (
        <>
          <VideoFrame videoId="h7-niOvadgg" title="Vidéo publicitaire en motion design — AXA" />
          <a
            href="https://souslesdraps.org"
            target="_blank"
            rel="noreferrer"
            aria-label="Sous Les DRAPS — ouvrir le site dans un nouvel onglet"
            className="block group"
          >
            <Frame>
              <img
                src="/souslesdraps.png"
                alt="Plateforme e-learning Sous Les DRAPS"
                className="w-full h-60 object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </Frame>
          </a>
        </>
      ),
    },
    {
      id: 'modelisation',
      index: '03',
      title: 'Modélisation 3D',
      items: [
        'Images de synthèse',
        "Films d'animation",
        'Environnements augmentés',
        'Réalité virtuelle',
        'Simulation immersive',
        'Fichiers d’usinage pour machines à commandes numériques (CNC)',
        'Impression 3D',
      ],
      media: (
        <>
          <ImgFrame src="/decors3.png" alt="Rendu 3D de décor" />
          <ImgFrame src="/decors2-1.png" alt="Rendu 3D de décor en modélisation" />
        </>
      ),
    },
    {
      id: 'sensibilisation',
      index: '04',
      title: 'Sensibilisation handicap',
      reverse: true,
      items: [
        'Fresque collective',
        'Fresque collective numérique',
        'Sculpture collective',
        'Mise en situation',
        'Prestations sur-mesure',
      ],
      media: (
        <>
          <ImgFrame src="/photo sensibilisation sculpture.jpg" alt="Atelier de sculpture collective de sensibilisation au handicap" />
          <ImgFrame src="/sensibilisation au handicap photo.jpg" alt="Atelier de sensibilisation au handicap" />
        </>
      ),
    },
    {
      id: 'formation',
      index: '05',
      title: 'Formations',
      items: [
        "Formation à l'accueil de personnes en situation de handicap",
        'Formation au FALC',
        'PDF accessible',
        'Formation à la communication digitale universelle',
      ],
      media: (
        <>
          <ImgFrame src="/foch.jpg" alt="Formation à l'accueil des personnes en situation de handicap — Hôpital Foch" />
          <ImgFrame src="/sensi EY.jpg" alt="Atelier de sensibilisation au handicap chez Ernst & Young" />
        </>
      ),
    },
  ]

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="relative overflow-hidden pt-36 pb-10 lg:pt-44 lg:pb-16">
        <Atmosphere variant="top" />
        <div className="container-pdv relative z-10">
          <Reveal className="max-w-3xl">
            <span className="eyebrow">Nos prestations</span>
            <h1 className="mt-5 font-display font-extrabold leading-[1] text-[clamp(2.4rem,6vw,4.5rem)]">
              Ce que nous <span className="text-gradient">créons</span>
            </h1>
            <p className="mt-6 text-lg text-[#b6a9d8]">
              Par son agrément « Entreprise Adaptée », Place des Victoires permet aux entreprises de
              +20 salariés de respecter leurs obligations d'emploi de travailleurs handicapés tout en
              réduisant une partie de leur taxe AGEFIPH.
            </p>
            <button onClick={openContact} className="btn btn-primary mt-8 group">
              Contactez-nous
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </Reveal>
        </div>
      </section>

      <div className="divide-y divide-white/5">
        {sections.map((s) => (
          <ServiceBlock key={s.id} {...s} />
        ))}
      </div>
    </div>
  )
}

export default PagePrestations
